import { useIsCmdShiftHOverlayEnabled, useIsRpaAgentEnabled } from '@/features/flags';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { rpaBridge } from '@heidi/plugin-rpa';
import { useEffect } from 'react';

/**
 * Sync desktop overlay window enablement and summon-hotkey registration into
 * Rust via the RPA bridge.
 *
 * Window enable stays on RPA entitlement so computer-use / agent paths can
 * pre-warm the overlay. Hotkey registration is driven only by the Cmd+Shift+H
 * kill switch so agent callers that force-enable the window cannot re-arm the
 * shortcut while the flag is off.
 *
 * While auth is still resolving, prior state is preserved (no transient revoke).
 */
export function useOverlaySetEnabled({
  authenticated = false,
}: {
  authenticated?: boolean;
}) {
  const isRpaAgentEnabled = useIsRpaAgentEnabled();
  const isCmdShiftHOverlayEnabled = useIsCmdShiftHOverlayEnabled();

  useEffect(() => {
    if (!isTauri()) return;

    rpaBridge
      .overlaySetEnabled(isRpaAgentEnabled && authenticated)
      .catch((err: unknown) => {
        logger.error('[overlay] Failed to set overlay enabled state', { err });
      });
  }, [isRpaAgentEnabled, authenticated]);

  useEffect(() => {
    if (!isTauri()) return;

    rpaBridge
      .overlaySetHotkeyEnabled(isCmdShiftHOverlayEnabled && authenticated)
      .catch((err: unknown) => {
        logger.error('[overlay] Failed to set overlay hotkey enabled state', {
          err,
        });
      });
  }, [isCmdShiftHOverlayEnabled, authenticated]);
}
